import { inject, Injectable, OnDestroy } from '@angular/core';
import { catchError, combineLatest, firstValueFrom, Observable, of, ReplaySubject, Subject, takeUntil } from 'rxjs';
import { TenantContextService } from 'src/app/shared/tenant-context/tenant-context.service';
import { IMultiTenantSystemPermissions, ISystemPermissions } from '../../TODO/interfaces';
import { AccountService } from '../account.service';

@Injectable({
    providedIn: 'root'
})
export class TenantPermissionsSwitchService implements OnDestroy {
    private _permissions: ISystemPermissions | null = null;
    private currentTenantCode = '';
    private accountService = inject(AccountService);
    private tenantContext = inject(TenantContextService);
    private latest$ = new ReplaySubject<ISystemPermissions | null>(1);
    private allPermissions$ = new ReplaySubject<IMultiTenantSystemPermissions[] | null>(1);
    private _multiTenantSystemPermissions: IMultiTenantSystemPermissions[];
    private destroy$ = new Subject<void>();

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public initTenantSystemPermissions(): void {
        this.onTenantChange();
    }

    private onTenantChange() {
        const tenant = this.tenantContext.tenantSelected$;
        const multiTenantSystemPermissions = this.accountService.getSystemPermissions().pipe(
            catchError(err => {
                console.error('getSystemPermissions failed', err);
                return of([]);
            })
        );

        combineLatest([multiTenantSystemPermissions, tenant])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([p, t]) => {
                this._multiTenantSystemPermissions = p;
                this.allPermissions$.next(p);
                if (this.currentTenantCode !== t?.tenantId) {
                    this.currentTenantCode = t?.tenantId || '';
                }
                this.switchTenantSystemPermissions();
            });
    }
    private switchTenantSystemPermissions() {
        const permissionsForTenant = this._multiTenantSystemPermissions.find(
            p => p.tenantId === this.currentTenantCode
        );
        if (permissionsForTenant) {
            this._permissions = permissionsForTenant.systemPermissions;
        } else {
            // TODO: this returns default permissions when permissions feature flag is off
            // OR if mock permissions is turned on
            const defaultPermissions = this._multiTenantSystemPermissions.find(p => p.tenantId === 'default');
            if (defaultPermissions) {
                this._permissions = defaultPermissions.systemPermissions;
            } else {
                this._permissions = null;
            }
        }
        this.latest$.next(this._permissions);
    }

    public getLatestTenant(): Observable<ISystemPermissions | null> {
        return this.latest$;
    }
    public getAllPermissions(): Observable<IMultiTenantSystemPermissions[] | null> {
        return this.allPermissions$;
    }
    public async getCurrentTenantPermissions(): Promise<ISystemPermissions | null> {
        return await firstValueFrom(this.latest$);
    }
}
